/**
 * /reset-password?token=… — password reset landing (los-auth-migration.md §9).
 *
 * On mount, verifies the token (GET /auth/password/reset/verify). If valid,
 * shows the new-password form; on submit (POST /auth/password/reset/complete)
 * the server auto-logs-in (returns a session token), which we hand to
 * AuthProvider.login() and redirect home. Expired/used/unknown → error + a
 * path back to /login.
 */
import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { authApi } from "../api/authApi";
import { useAuth } from "../auth/AuthProvider";
import EnvLogo from "../components/env/EnvLogo";
import Flx from "../components/layout/Flx";

const ResetPasswordScreen = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") || "";

  const [phase, setPhase] = useState("verifying"); // verifying | valid | invalid | done
  const [email, setEmail] = useState(null);
  const [verifyError, setVerifyError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const fieldType = show ? "text" : "password";
  const revealAdornment = {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => setShow((v) => !v)}
          edge="end"
          tabIndex={-1}
          size="small"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? (
            <VisibilityOff fontSize="small" />
          ) : (
            <Visibility fontSize="small" />
          )}
        </IconButton>
      </InputAdornment>
    ),
  };

  useEffect(() => {
    if (!token) {
      setPhase("invalid");
      setVerifyError("Missing reset token.");
      return;
    }
    authApi
      .verifyPasswordReset(token)
      .then((res) => {
        if (res?.valid) {
          setEmail(res.email ?? null);
          setPhase("valid");
        } else {
          setPhase("invalid");
          setVerifyError(res?.error || "This reset link is no longer valid.");
        }
      })
      .catch((err) => {
        setPhase("invalid");
        setVerifyError(
          err?.response?.data?.error ||
            err?.message ||
            "This reset link is invalid or has expired.",
        );
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const res = await authApi.completePasswordReset(token, password);
      const sessionToken = res?.session_token ?? res?.access_token;
      if (sessionToken) {
        await login(sessionToken);
        navigate("/", { replace: true });
      } else {
        // No auto-login token returned — send them to sign in.
        setPhase("done");
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to reset password.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Flx center column sx={{ p: 2, minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Card sx={{ width: 400, maxWidth: "100%", p: 4 }}>
        <Flx center column sx={{ mb: 2 }}>
          <EnvLogo maxHeight={56} maxWidth={180} />
        </Flx>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          Reset password
        </Typography>

        {phase === "verifying" && (
          <Flx center sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Flx>
        )}

        {phase === "invalid" && (
          <Stack spacing={2}>
            <Alert severity="error">{verifyError}</Alert>
            <Link component={RouterLink} to="/login">
              Back to sign in
            </Link>
          </Stack>
        )}

        {phase === "done" && (
          <Stack spacing={2}>
            <Alert severity="success">
              Your password has been reset. Please sign in.
            </Alert>
            <Link component={RouterLink} to="/login">
              Go to sign in
            </Link>
          </Stack>
        )}

        {phase === "valid" && (
          <Stack component="form" onSubmit={submit} spacing={2}>
            {email && (
              <Typography variant="body2" color="text.secondary">
                Setting a new password for <strong>{email}</strong>.
              </Typography>
            )}
            <TextField
              label="New password"
              type={fieldType}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              helperText="At least 12 characters."
              InputProps={revealAdornment}
            />
            <TextField
              label="Confirm password"
              type={fieldType}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              fullWidth
              InputProps={revealAdornment}
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" variant="contained" disabled={busy} fullWidth>
              {busy ? "Setting password…" : "Set password"}
            </Button>
            <Link component={RouterLink} to="/login">
              Back to sign in
            </Link>
          </Stack>
        )}
      </Card>
    </Flx>
  );
};

export default ResetPasswordScreen;
